import type * as Ariakit from '@ariakit/react';
import cn from 'classnames';
import { useState } from 'react';
import { QuestionCircle } from '#assets/ReactSvg.ts';
import { useGuaranteedId } from '#hooks/useGuaranteedId.ts';
import { useTimeout } from '#hooks/useTimeout.ts';
import { Tooltip } from '../../tooltip/Tooltip.tsx';
import styles from './HelpTip.module.css';

type Props = {
  children: React.ReactNode;
  className?: string;
  portal?: Ariakit.TooltipProps['portal'];
};

/**
 * {@link HelpTip} is a toggletip that displays extra contextual information that might help the
 * user understand a concept.  It opens on click (or keyboard press), and closes when clicking
 * or tabbing out.  Escape also closes.
 *
 * Ref: https://inclusive-components.design/tooltips-toggletips/
 */
export function HelpTip({ children, className, portal }: Props) {
  const descId = useGuaranteedId({ prefix: 'help' });
  const [open, setOpen] = useState<boolean>(false);
  const [hideOnHoverOutside, setHideOnHoverOutside] = useState<boolean>(true);
  const { start: showTooltipAfterDelay, clear: cancelTooltipShow } = useTimeout(() => {
    setOpen(true);
  }, 500);

  return (
    <>
      <Tooltip
        content={children}
        portal={portal}
        onClick={() => {
          setOpen(true);
          setHideOnHoverOutside(false);
        }}
        onMouseEnter={() => {
          showTooltipAfterDelay();
          setHideOnHoverOutside(true);
        }}
        onMouseLeave={() => {
          if (open && hideOnHoverOutside) {
            setOpen(false);
          } else {
            cancelTooltipShow();
          }
        }}
        onBlur={() => {
          setOpen(false);
        }}
        onKeyDown={(event) => {
          if (open && event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
          }
        }}
        open={open}
        hideOnHoverOutside={hideOnHoverOutside}
        showOnHover={false}
        render={<button type="button" className={cn(className, styles.HelpTip)} aria-describedby={descId} />}
      >
        <span className="visually-hidden">Help tooltip</span>
        <span className={styles.HelpTip_icon}>
          <QuestionCircle />
        </span>
      </Tooltip>
      <span id={descId} className="visually-hidden">
        {children}
      </span>
    </>
  );
}
